import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
    LayoutDashboard,
    Cpu,
    MapPin,
    FileText,
    Bell,
    Settings,
    LogOut,
    Menu,
    X,
    ChevronDown,
    User,
    Building2,
    Users
} from 'lucide-react';
import { Button } from './ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Badge } from './ui/badge';

const getNavItems = (isMaster, isAdmin) => {
    if (isMaster) {
        return [
            { path: '/companies', icon: Building2, label: 'Empresas' },
            { path: '/users', icon: Users, label: 'Utilizadores' },
        ];
    }
    
    const items = [
        { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
        { path: '/devices', icon: Cpu, label: 'Dispositivos' },
        { path: '/areas', icon: MapPin, label: 'Áreas' },
        { path: '/records', icon: FileText, label: 'Registos' },
        { path: '/alerts', icon: Bell, label: 'Alertas' },
    ];
    
    if (isAdmin) {
        items.push({ path: '/users', icon: Users, label: 'Utilizadores' });
    }
    
    items.push({ path: '/settings', icon: Settings, label: 'Definições' });
    
    return items;
};

export const Layout = ({ children }) => {
    const { user, logout, isMaster, isAdmin } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [notifications] = useState([]);

    const navItems = getNavItems(isMaster, isAdmin);

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const getRoleBadge = () => {
        if (user?.role === 'master') return <Badge className="bg-amber-100 text-amber-700 text-xs">Master</Badge>;
        if (user?.role === 'admin') return <Badge className="bg-blue-100 text-blue-700 text-xs">Admin</Badge>;
        return null;
    };

    return (
        <div className="min-h-screen bg-slate-50 flex">
            {/* Mobile sidebar overlay */}
            {sidebarOpen && (
                <div
                    className="fixed inset-0 bg-black/50 z-40 lg:hidden"
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            {/* Sidebar */}
            <aside
                className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-slate-900 transform transition-transform duration-200 ease-in-out ${
                    sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
                }`}
            >
                <div className="flex flex-col h-full">
                    {/* Logo */}
                    <div className="flex items-center gap-3 px-6 py-5 border-b border-slate-800">
                        <img 
                            src="https://customer-assets.emergentagent.com/job_powermonitor-5/artifacts/rtabpwt5_Sem%20Fundo.png" 
                            alt="MedEletrões Logo" 
                            className="w-20 h-20 object-contain"
                        />
                        <div>
                            <h1 className="text-lg font-bold text-white font-heading">MedEletrões</h1>
                            <p className="text-xs text-slate-400">Gestão de Energia</p>
                        </div>
                        <button
                            className="lg:hidden ml-auto text-slate-400 hover:text-white"
                            onClick={() => setSidebarOpen(false)}
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Company/Role Info */}
                    <div className="px-4 py-3 border-b border-slate-800">
                        <div className="flex items-center gap-2">
                            <Building2 className="w-4 h-4 text-slate-500" />
                            <span className="text-sm text-slate-300 truncate">
                                {user?.company_name || 'Master'}
                            </span>
                        </div>
                    </div>

                    {/* Navigation */}
                    <nav className="flex-1 px-3 py-6 space-y-1 overflow-y-auto">
                        {navItems.map((item) => {
                            const isActive = location.pathname === item.path;
                            return (
                                <Link
                                    key={item.path}
                                    to={item.path}
                                    className={`sidebar-nav-item ${isActive ? 'active' : ''}`}
                                    onClick={() => setSidebarOpen(false)}
                                    data-testid={`nav-${item.path.slice(1)}`}
                                >
                                    <item.icon className="w-5 h-5" />
                                    <span className="font-medium">{item.label}</span>
                                </Link>
                            );
                        })}
                    </nav>

                    {/* User section */}
                    <div className="p-4 border-t border-slate-800">
                        <div className="flex items-center gap-3 px-3 py-2">
                            <div className="w-9 h-9 rounded-full bg-emerald-600 flex items-center justify-center">
                                <span className="text-sm font-semibold text-white">
                                    {user?.name?.charAt(0).toUpperCase()}
                                </span>
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                    <p className="text-sm font-medium text-white truncate">{user?.name}</p>
                                    {getRoleBadge()}
                                </div>
                                <p className="text-xs text-slate-400 truncate">{user?.email}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </aside>

            {/* Main content */}
            <div className="flex-1 flex flex-col min-w-0">
                {/* Header */}
                <header className="glass-header sticky top-0 z-30 px-4 lg:px-8 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <button
                                className="lg:hidden p-2 rounded-lg hover:bg-slate-100 transition-colors"
                                onClick={() => setSidebarOpen(true)}
                                data-testid="mobile-menu-btn"
                            >
                                <Menu className="w-5 h-5 text-slate-600" />
                            </button>
                            <div>
                                <h2 className="text-xl font-bold text-slate-900 font-heading">
                                    {navItems.find(item => item.path === location.pathname)?.label || 'Dashboard'}
                                </h2>
                            </div>
                        </div>

                        <div className="flex items-center gap-3">
                            {/* Notifications */}
                            {!isMaster && (
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="relative"
                                            data-testid="notifications-btn"
                                        >
                                            <Bell className="w-5 h-5 text-slate-600" />
                                            {notifications.length > 0 && (
                                                <Badge className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 bg-red-500">
                                                    {notifications.length}
                                                </Badge>
                                            )}
                                        </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end" className="w-80">
                                        <div className="px-4 py-3 border-b">
                                            <p className="font-semibold">Notificações</p>
                                        </div>
                                        {notifications.length === 0 ? (
                                            <div className="px-4 py-8 text-center text-slate-500">
                                                <Bell className="w-8 h-8 mx-auto mb-2 opacity-50" />
                                                <p className="text-sm">Sem notificações</p>
                                            </div>
                                        ) : (
                                            notifications.map((notif) => (
                                                <DropdownMenuItem key={notif.id}>
                                                    {notif.message}
                                                </DropdownMenuItem>
                                            ))
                                        )}
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            )}

                            {/* User menu */}
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button
                                        variant="ghost"
                                        className="flex items-center gap-2"
                                        data-testid="user-menu-btn"
                                    >
                                        <div className="w-8 h-8 rounded-full bg-emerald-600 flex items-center justify-center">
                                            <span className="text-sm font-semibold text-white">
                                                {user?.name?.charAt(0).toUpperCase()}
                                            </span>
                                        </div>
                                        <span className="hidden md:block text-sm font-medium text-slate-700">
                                            {user?.name}
                                        </span>
                                        <ChevronDown className="w-4 h-4 text-slate-500" />
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-56">
                                    <div className="px-4 py-3 border-b">
                                        <div className="flex items-center gap-2">
                                            <p className="font-semibold">{user?.name}</p>
                                            {getRoleBadge()}
                                        </div>
                                        <p className="text-sm text-slate-500">{user?.email}</p>
                                        <p className="text-xs text-slate-400 mt-1">{user?.company_name}</p>
                                    </div>
                                    {!isMaster && (
                                        <>
                                            <DropdownMenuItem onClick={() => navigate('/settings')}>
                                                <User className="w-4 h-4 mr-2" />
                                                Perfil
                                            </DropdownMenuItem>
                                            <DropdownMenuItem onClick={() => navigate('/settings')}>
                                                <Settings className="w-4 h-4 mr-2" />
                                                Definições
                                            </DropdownMenuItem>
                                            <DropdownMenuSeparator />
                                        </>
                                    )}
                                    <DropdownMenuItem
                                        onClick={handleLogout}
                                        className="text-red-600 focus:text-red-600"
                                        data-testid="logout-btn"
                                    >
                                        <LogOut className="w-4 h-4 mr-2" />
                                        Terminar Sessão
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </div>
                    </div>
                </header>

                {/* Page content */}
                <main className="flex-1 p-4 lg:p-8 overflow-auto">
                    <div className="max-w-7xl mx-auto animate-fade-in">
                        {children}
                    </div>
                </main>

                {/* Footer */}
                <footer className="px-4 lg:px-8 py-3 border-t border-slate-200 bg-white">
                    <div className="flex justify-end">
                        <p className="text-xs text-slate-500">
                            Desenvolvido por <a href="https://www.inforexe.com" target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:text-emerald-700 font-medium">Infor.Exe</a>
                        </p>
                    </div>
                </footer>
            </div>
        </div>
    );
};
